/**
 * Real object detection for the ball tracker: a YOLOv8-nano model
 * fine-tuned specifically on golf balls (17,460 CC BY 4.0 licensed images,
 * mAP50 91% / mAP50-95 53% on the held-out validation split), run
 * client-side via ONNX Runtime Web (WASM backend). This replaces an
 * earlier generic 80-class COCO-SSD model, which almost never detected a
 * small, distant golf ball at all — being trained specifically on golf
 * balls (rather than "sports ball" as one of 80 unrelated categories)
 * makes a real difference for an object this small and specific.
 *
 * The model (~12MB) and the ONNX runtime are loaded lazily via dynamic
 * import, only once the ball-flight page is actually used, so the rest of
 * the app's initial bundle stays unaffected.
 */

import type { InferenceSession } from "onnxruntime-web";

// Training data was resized with a plain stretch to 416x416 (no letterbox
// padding), so inference must reproduce that exact transform — padding
// instead would feed the model a distribution it never saw.
const MODEL_INPUT_SIZE = 416;
const CONFIDENCE_THRESHOLD = 0.15;
// Detections within this normalized distance of a higher-scoring one are
// treated as the same object (the model fires on several nearby anchors
// per real ball) and dropped, cheap stand-in for IoU-based NMS given how
// small and simple (round, single-class) these boxes are.
const DEDUPE_RADIUS = 0.04;

let sessionPromise: Promise<InferenceSession> | null = null;

/** Loads (once, cached) the ONNX Runtime WASM backend + the golf ball detection model. */
export function loadBallDetectorModel(): Promise<InferenceSession> {
  if (!sessionPromise) {
    sessionPromise = (async () => {
      // The "/wasm" entry point pulls in only the plain WASM execution
      // provider (~13MB); the default entry bundles WebGPU/WebNN support
      // too, which drags in a much larger (~27MB) wasm binary we don't need.
      const ort = await import("onnxruntime-web/wasm");
      const base = import.meta.env.BASE_URL;
      ort.env.wasm.wasmPaths = `${base}ort/`;
      // The bundled wasm binary is a threaded build, but multi-threading
      // needs cross-origin-isolation headers (COOP/COEP) that a plain
      // static host like GitHub Pages doesn't send — forcing 1 thread
      // keeps it running correctly (just without multi-threading) instead
      // of silently failing to use SharedArrayBuffer.
      ort.env.wasm.numThreads = 1;
      return ort.InferenceSession.create(`${base}models/golfball-yolov8n.onnx`, {
        executionProviders: ["wasm"],
      });
    })();
  }
  return sessionPromise;
}

export interface BallCandidate {
  /** Relative 0..1 position within the frame */
  x: number;
  y: number;
  score: number;
}

/** Creates an offscreen canvas used to capture video frames for detection. */
export function makeWorkCanvas(): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = MODEL_INPUT_SIZE;
  c.height = MODEL_INPUT_SIZE;
  return c;
}

/** Draws the current video frame into the work canvas, stretched to the model's fixed input size. */
export function drawFrame(source: CanvasImageSource, canvas: HTMLCanvasElement): void {
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D wird nicht unterstützt");
  ctx.drawImage(source, 0, 0, MODEL_INPUT_SIZE, MODEL_INPUT_SIZE);
}

/**
 * Draws only the upper `topFraction` of the source frame, stretched to
 * fill the model's fixed input size. The model resizes whatever it's
 * given down to its own fixed processing grid, so a small, distant ball
 * occupies a bigger — and thus more detectable — share of that grid once
 * the (usually irrelevant) foreground/ground portion of the frame is
 * cropped away first.
 */
export function drawFrameCropped(
  source: CanvasImageSource,
  canvas: HTMLCanvasElement,
  sourceW: number,
  sourceH: number,
  topFraction: number
): void {
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D wird nicht unterstützt");
  const cropH = Math.max(1, Math.round(sourceH * topFraction));
  ctx.drawImage(source, 0, 0, sourceW, cropH, 0, 0, MODEL_INPUT_SIZE, MODEL_INPUT_SIZE);
}

function dedupe(candidates: BallCandidate[]): BallCandidate[] {
  const sorted = [...candidates].sort((a, b) => b.score - a.score);
  const kept: BallCandidate[] = [];
  for (const c of sorted) {
    if (kept.some((k) => Math.hypot(k.x - c.x, k.y - c.y) < DEDUPE_RADIUS)) continue;
    kept.push(c);
  }
  return kept;
}

/**
 * Runs the detector on one frame (already drawn into the 416x416 work
 * canvas) and returns every golf-ball detection as a normalized center
 * point. There can be several — spare balls lying in the grass, the one
 * actually in flight — trajectory fitting downstream (RANSAC across many
 * frames) is what picks out the one that's really moving.
 */
export async function detectBallsInFrame(session: InferenceSession, canvas: HTMLCanvasElement): Promise<BallCandidate[]> {
  const ort = await import("onnxruntime-web/wasm");
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D wird nicht unterstützt");
  const { data } = ctx.getImageData(0, 0, MODEL_INPUT_SIZE, MODEL_INPUT_SIZE);

  const n = MODEL_INPUT_SIZE * MODEL_INPUT_SIZE;
  const chw = new Float32Array(3 * n);
  for (let i = 0; i < n; i++) {
    const o = i * 4;
    chw[i] = data[o] / 255;
    chw[n + i] = data[o + 1] / 255;
    chw[2 * n + i] = data[o + 2] / 255;
  }

  const input = new ort.Tensor("float32", chw, [1, 3, MODEL_INPUT_SIZE, MODEL_INPUT_SIZE]);
  const results = await session.run({ images: input });
  const output = results.output0.data as Float32Array;

  // Layout is [1, 5, numAnchors]: rows 0-3 are box cx,cy,w,h in pixels of
  // the 416x416 input, row 4 is the (already sigmoid-activated) class
  // confidence — single class, so no separate objectness score to combine.
  const numAnchors = output.length / 5;
  const candidates: BallCandidate[] = [];
  for (let i = 0; i < numAnchors; i++) {
    const score = output[4 * numAnchors + i];
    if (score < CONFIDENCE_THRESHOLD) continue;
    const cx = output[i];
    const cy = output[numAnchors + i];
    candidates.push({ x: cx / MODEL_INPUT_SIZE, y: cy / MODEL_INPUT_SIZE, score });
  }

  return dedupe(candidates);
}

/**
 * Runs detection twice per frame — once on the full frame, once zoomed
 * into its upper 70% (full width) — and merges the results, mapping the
 * zoomed pass's coordinates back into full-frame space. A ball already in
 * flight is almost always in this upper region; the doubled inference
 * cost buys real recall for small/distant balls that the full-frame pass
 * alone misses.
 */
export async function detectBallsTiled(
  session: InferenceSession,
  source: CanvasImageSource,
  sourceW: number,
  sourceH: number,
  fullCanvas: HTMLCanvasElement,
  cropCanvas: HTMLCanvasElement
): Promise<BallCandidate[]> {
  const topFraction = 0.7;

  drawFrame(source, fullCanvas);
  const full = await detectBallsInFrame(session, fullCanvas);

  drawFrameCropped(source, cropCanvas, sourceW, sourceH, topFraction);
  const zoomedRaw = await detectBallsInFrame(session, cropCanvas);
  const zoomed = zoomedRaw.map((c) => ({ ...c, y: c.y * topFraction }));

  const merged = [...full];
  for (const c of zoomed) {
    if (!merged.some((m) => Math.hypot(m.x - c.x, m.y - c.y) < DEDUPE_RADIUS)) merged.push(c);
  }
  return merged;
}

/** Picks the candidate closest to a predicted position, or the highest-confidence one if there's no prediction yet. */
export function pickBestCandidate(
  candidates: BallCandidate[],
  predicted: { x: number; y: number } | null
): BallCandidate | null {
  if (candidates.length === 0) return null;
  if (!predicted) {
    return candidates.reduce((best, c) => (c.score > best.score ? c : best), candidates[0]);
  }
  let best = candidates[0];
  let bestDist = Infinity;
  for (const c of candidates) {
    const d = (c.x - predicted.x) ** 2 + (c.y - predicted.y) ** 2;
    if (d < bestDist) {
      bestDist = d;
      best = c;
    }
  }
  return best;
}
